/**
 * Brand Setup Modal
 * Prompts users to complete their brand profile after creation
 */

import { useState } from 'react';
import { Icon } from './icons';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import type { Brand } from '../types';
import type { MissingField } from '../utils/brand-completeness';

interface BrandSetupModalProps {
  brand: Brand;
  missingFields: MissingField[];
  completenessPercentage: number;
  onClose: () => void;
  onStartAnalysis: (websiteUrl: string) => Promise<void>;
  onSkip: () => void;
  onNavigateToDNA: () => void;
}

type ModalStep = 'intro' | 'website' | 'analyzing' | 'complete' | 'manual';

export function BrandSetupModal({
  brand,
  missingFields,
  completenessPercentage,
  onClose,
  onStartAnalysis,
  onSkip,
  onNavigateToDNA,
}: BrandSetupModalProps) {
  const [step, setStep] = useState<ModalStep>(brand.website_url ? 'intro' : 'website');
  const [websiteUrl, setWebsiteUrl] = useState(brand.website_url || '');
  const [error, setError] = useState<string | null>(null);

  const highPriorityMissing = missingFields.filter((f) => f.priority === 'high');

  const handleAnalyze = async () => {
    if (!websiteUrl.trim()) return;

    setError(null);
    setStep('analyzing');

    try {
      await onStartAnalysis(websiteUrl);
      setStep('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
      setStep('website');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header gradient */}
        <div
          className="h-2"
          style={{
            background: `linear-gradient(90deg, ${brand.color_primary}, ${brand.color_secondary})`,
          }}
        />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <Icon name="x" size="md" />
        </button>

        <div className="p-6">
          {/* Intro Step */}
          {step === 'intro' && (
            <>
              <div className="text-center mb-6">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4"
                  style={{ backgroundColor: brand.color_primary }}
                >
                  {brand.name[0]}
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  {brand.name} Created!
                </h2>
                <p className="text-gray-600 mt-2">
                  Let's set up your brand profile so AI can generate on-brand content.
                </p>
              </div>

              {/* Completeness indicator */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Profile Completeness
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    {completenessPercentage}%
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${completenessPercentage}%`,
                      backgroundColor: brand.color_primary,
                    }}
                  />
                </div>
              </div>

              {/* Missing high priority items */}
              {highPriorityMissing.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm text-gray-500 mb-2">What's needed:</p>
                  <ul className="space-y-2">
                    {highPriorityMissing.slice(0, 3).map((field) => (
                      <li
                        key={field.field}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        {field.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3">
                {brand.website_url ? (
                  <Button className="w-full" onClick={handleAnalyze}>
                    <Icon name="sparkles" size="sm" className="mr-2" />
                    Analyze Website with AI
                    <Icon name="arrow-right" size="sm" className="ml-2" />
                  </Button>
                ) : (
                  <Button className="w-full" onClick={() => setStep('website')}>
                    <Icon name="globe" size="sm" className="mr-2" />
                    Add Website for AI Setup
                    <Icon name="arrow-right" size="sm" className="ml-2" />
                  </Button>
                )}

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setStep('manual')}
                >
                  Set Up Manually
                </Button>

                <button
                  onClick={onSkip}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
                >
                  I'll do this later
                </button>
              </div>
            </>
          )}

          {/* Website Step */}
          {step === 'website' && (
            <>
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                  <Icon name="globe" size="lg" className="text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Add Your Website
                </h2>
                <p className="text-gray-600 mt-2">
                  We'll analyze your website to extract your brand's voice, tone, and style.
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <Input
                  label="Website URL"
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yourbrand.com"
                  autoFocus
                />

                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <Icon name="alert" size="sm" />
                    {error}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <Button
                  className="w-full"
                  onClick={handleAnalyze}
                  disabled={!websiteUrl.trim()}
                >
                  <Icon name="sparkles" size="sm" className="mr-2" />
                  Analyze with AI
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setStep('manual')}
                >
                  Skip - Set Up Manually
                </Button>
              </div>
            </>
          )}

          {/* Analyzing Step */}
          {step === 'analyzing' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-6">
                <Icon name="loader" size="xl" className="text-purple-600 animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Analyzing Your Brand
              </h2>
              <p className="text-gray-600">
                Extracting tone, voice, and content patterns...
              </p>

              <div className="mt-6 space-y-2 text-sm text-gray-500">
                <p className="flex items-center justify-center gap-2">
                  <Icon name="check" size="sm" className="text-green-500" />
                  Fetching website content
                </p>
                <p className="flex items-center justify-center gap-2">
                  <Icon name="loader" size="sm" className="animate-spin" />
                  Analyzing brand voice
                </p>
                <p className="flex items-center justify-center gap-2 text-gray-400">
                  <div className="w-4 h-4" />
                  Extracting content patterns
                </p>
              </div>
            </div>
          )}

          {/* Complete Step */}
          {step === 'complete' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Icon name="check" size="xl" className="text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Brand DNA Extracted!
              </h2>
              <p className="text-gray-600 mb-6">
                Your brand profile has been automatically populated. You can review and fine-tune it anytime.
              </p>

              <div className="space-y-3">
                <Button className="w-full" onClick={onNavigateToDNA}>
                  Review Brand DNA
                  <Icon name="arrow-right" size="sm" className="ml-2" />
                </Button>

                <Button variant="outline" className="w-full" onClick={onClose}>
                  Start Creating Content
                </Button>
              </div>
            </div>
          )}

          {/* Manual Step */}
          {step === 'manual' && (
            <>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Manual Setup
                </h2>
                <p className="text-gray-600 mt-2">
                  Fill in your brand details in the Brand DNA section.
                </p>
              </div>

              {/* Missing fields by category */}
              <div className="space-y-4 mb-6">
                {['voice', 'audience', 'content'].map((category) => {
                  const categoryFields = missingFields.filter(
                    (f) => f.category === category
                  );
                  if (categoryFields.length === 0) return null;

                  return (
                    <div key={category} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 capitalize mb-2">
                        {category === 'voice' ? 'Brand Voice' : category}
                      </h4>
                      <ul className="space-y-1">
                        {categoryFields.map((field) => (
                          <li
                            key={field.field}
                            className="text-sm text-gray-600 flex items-center gap-2"
                          >
                            <div
                              className={`w-1.5 h-1.5 rounded-full ${
                                field.priority === 'high'
                                  ? 'bg-red-500'
                                  : field.priority === 'medium'
                                  ? 'bg-amber-500'
                                  : 'bg-gray-400'
                              }`}
                            />
                            {field.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              <div className="space-y-3">
                <Button className="w-full" onClick={onNavigateToDNA}>
                  Go to Brand DNA
                  <Icon name="arrow-right" size="sm" className="ml-2" />
                </Button>

                <button
                  onClick={onSkip}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
                >
                  I'll do this later
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
